/**
 * The transport: play, pause, stop, seek, and the tap-tempo tracker.
 *
 * Measures are scheduled one at a time rather than all at once, so a tempo
 * change — including one made by tapping — takes effect on the very next
 * measure of an ongoing playback.
 */

import { create } from 'zustand';
import { measureCount, midiOf } from '../core';
import { audio, TAP_TIMEOUT_MS, TEMPO_MAX, TEMPO_MIN, tapTempo } from '../audio/engine';
import { useSession } from './store';

export type TransportStatus = 'stopped' | 'playing' | 'paused';

interface PlaybackState {
  status: TransportStatus;
  /** Measure the playhead sits on, or null when the transport is idle. */
  playhead: number | null;
  /** Tempo shown beside the tap button; null once it has faded. */
  tapDisplay: number | null;

  play: () => void;
  pause: () => void;
  stop: () => void;
  toggle: () => void;
  /** Jump to a measure. While playing it re-cues; while stopped it sets the start point. */
  seek: (measure: number) => void;
  /** Register one tap on the tempo button. Sounds a click and may set the tempo. */
  tap: () => void;
  /** Hide the tap-tempo readout. Called by its own timer. */
  clearTapDisplay: () => void;
}

let timer: number | null = null;
let taps: number[] = [];
let tapFade: number | null = null;

/** Milliseconds per semibreve at the current tempo. */
const measureMs = (): number => 60000 / useSession.getState().settings.tempo;

function clearTimer(): void {
  if (timer != null) window.clearTimeout(timer);
  timer = null;
}

export const usePlayback = create<PlaybackState>((set, get) => ({
  status: 'stopped',
  playhead: null,
  tapDisplay: null,

  play: () => {
    const session = useSession.getState();
    audio.unlock();
    const from = get().status === 'paused' && get().playhead != null ? get().playhead! : session.playStart;
    set({ status: 'playing', playhead: Math.min(from, measureCount(session.exercise) - 1) });
    step(set, get);
  },

  pause: () => {
    if (get().status !== 'playing') return;
    clearTimer();
    audio.silence();
    set({ status: 'paused' });
  },

  stop: () => {
    clearTimer();
    audio.silence();
    set({ status: 'stopped', playhead: null });
  },

  toggle: () => {
    const { status } = get();
    if (status === 'playing') get().pause();
    else get().play();
  },

  seek: (measure) => {
    const session = useSession.getState();
    const m = Math.min(Math.max(measure, 0), measureCount(session.exercise) - 1);
    session.setPlayStart(m);
    if (get().status === 'stopped') {
      set({ playhead: null });
      return;
    }
    clearTimer();
    audio.silence();
    set({ playhead: m });
    if (get().status === 'playing') step(set, get);
  },

  tap: () => {
    audio.unlock();
    const now = performance.now();
    if (taps.length && now - taps[taps.length - 1] > TAP_TIMEOUT_MS) taps = [];
    taps.push(now);
    audio.click(audio.now() + 0.005, true);

    const bpm = tapTempo(taps);
    if (bpm != null) {
      const clamped = Math.min(Math.max(bpm, TEMPO_MIN), TEMPO_MAX);
      useSession.getState().updateSettings({ tempo: clamped });
      set({ tapDisplay: clamped });
    } else {
      set({ tapDisplay: useSession.getState().settings.tempo });
    }

    if (tapFade != null) window.clearTimeout(tapFade);
    tapFade = window.setTimeout(() => get().clearTapDisplay(), 3000);
  },

  clearTapDisplay: () => set({ tapDisplay: null }),
}));

/**
 * Sound one measure and queue the next. The final measure is held for two beats
 * so the cadence lands, then the transport returns to rest.
 */
function step(set: (p: Partial<PlaybackState>) => void, get: () => PlaybackState): void {
  const session = useSession.getState();
  const ex = session.exercise;
  const n = measureCount(ex);
  const i = get().playhead;
  if (i == null || get().status !== 'playing') return;

  if (i >= n) {
    timer = window.setTimeout(() => get().stop(), measureMs());
    return;
  }

  const beat = measureMs();
  const seconds = ((i === n - 1 ? 2 : 1.02) * beat) / 1000;
  const at = audio.now() + 0.02;
  const voices = session.settings.playbackVoices;

  if (voices !== 'cp' && ex.cf[i] != null) audio.note(midiOf(ex.cf[i], i, ex), at, seconds, 'cf');
  if (voices !== 'cf' && ex.cp[i] != null) audio.note(midiOf(ex.cp[i]!, i, ex), at, seconds, 'cp');
  if (session.settings.metronomeDuringPlayback) audio.click(at, i === 0);

  // The playhead stays on the sounding measure and advances when it ends.
  timer = window.setTimeout(() => {
    if (get().status !== 'playing') return;
    set({ playhead: i + 1 });
    step(set, get);
  }, beat);
}

/** Sound a single cantus firmus note — the measure-strip audition. */
export function auditionCantus(measure: number): void {
  const ex = useSession.getState().exercise;
  if (ex.cf[measure] == null) return;
  audio.unlock();
  audio.note(midiOf(ex.cf[measure], measure, ex), audio.now() + 0.02, 0.8, 'cf');
}
